import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { propertiesApi, districtsApi } from '../services/api';
import { Plus, Edit, Trash2, Building2, Home, Store } from 'lucide-react';

const AdminPage = () => {
  const { t, getLocalizedText } = useLanguage();
  const { isAdmin } = useAuth();
  const [propertyList, setPropertyList] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const emptyProperty = {
    title: { en: '', ru: '', uz: '' },
    type: 'apartment',
    price: 0,
    price_type: 'sale',
    district_id: '',
    address: { en: '', ru: '', uz: '' },
    lat: 41.2995,
    lng: 69.2401,
    bedrooms: 1,
    bathrooms: 1,
    area: 50,
    floor: 1,
    total_floors: 5,
    year: 2024,
    parking: false,
    features: [],
    images: ['/placeholder.jpg'],
    description: { en: '', ru: '', uz: '' },
    badge: null,
    is_new: true
  };

  const [formData, setFormData] = useState(emptyProperty);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [propertiesData, districtsData] = await Promise.all([
        propertiesApi.getAll(),
        districtsApi.getAll()
      ]);
      setPropertyList(propertiesData);
      setDistricts(districtsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData(emptyProperty);
    setEditingProperty(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (property) => {
    setFormData({
      ...property,
      price_type: property.price_type || property.priceType,
      district_id: property.district_id || property.district,
      total_floors: property.total_floors || property.totalFloors,
      is_new: property.is_new || property.isNew
    });
    setEditingProperty(property.property_id || property.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await propertiesApi.delete(id);
        setPropertyList(propertyList.filter(p => (p.property_id || p.id) !== id));
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Failed to delete property');
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingProperty) {
        const updated = await propertiesApi.update(editingProperty, formData);
        setPropertyList(propertyList.map(p => 
          (p.property_id || p.id) === editingProperty ? updated : p
        ));
      } else {
        const created = await propertiesApi.create(formData);
        setPropertyList([created, ...propertyList]);
      }
      setIsDialogOpen(false);
      setFormData(emptyProperty);
      setEditingProperty(null);
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Failed to save property. Make sure you have admin privileges.');
    } finally {
      setSaving(false);
    }
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateLocalizedField = (field, lang, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value }
    }));
  };

  if (!isAdmin) {
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
          <p className="text-gray-500 mt-2">Login with Google to get admin access (first user becomes admin).</p>
        </div>
        <Footer />
        <Footer />
      </div>
    );
  }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('adminPanel')}</h1>
              <p className="text-gray-600">Manage properties and listings</p>
            </div>
            <Button onClick={handleAddNew} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4" />
              {t('addProperty')}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{propertyList.length}</p>
                    <p className="text-sm text-gray-500">Total Properties</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {propertyList.filter(p => p.type === 'house').length}
                    </p>
                    <p className="text-sm text-gray-500">{t('houses')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {propertyList.filter(p => p.type === 'apartment').length}
                    </p>
                    <p className="text-sm text-gray-500">{t('apartments')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Store className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {propertyList.filter(p => p.type === 'commercial').length}
                    </p>
                    <p className="text-sm text-gray-500">{t('commercial')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Properties Table */}
          <Card>
            <CardHeader>
              <CardTitle>Properties List</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-4 px-4 font-medium text-gray-600">Property</th>
                        <th className="text-left py-4 px-4 font-medium text-gray-600">Type</th>
                        <th className="text-left py-4 px-4 font-medium text-gray-600">District</th>
                        <th className="text-left py-4 px-4 font-medium text-gray-600">Price</th>
                        <th className="text-right py-4 px-4 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propertyList.map((property) => {
                        const propId = property.property_id || property.id;
                        const districtId = property.district_id || property.district;
                        const priceType = property.price_type || property.priceType;
                        return (
                          <tr key={propId} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <img
                                  src={property.images?.[0] || '/placeholder.jpg'}
                                  alt=""
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                                <div>
                                  <p className="font-medium text-gray-900 line-clamp-1">
                                    {getLocalizedText(property.title)}
                                  </p>
                                  <p className="text-sm text-gray-500 line-clamp-1">
                                    {getLocalizedText(property.address)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                property.type === 'apartment' ? 'bg-blue-100 text-blue-700' :
                                property.type === 'house' ? 'bg-green-100 text-green-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {property.type === 'apartment' ? t('apartments') :
                                 property.type === 'house' ? t('houses') : t('commercial')}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span className="text-gray-600">
                                {getLocalizedText(districts.find(d => d.district_id === districtId)?.name)}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <span className="font-medium text-gray-900">
                                ${property.price?.toLocaleString()}
                                {priceType === 'rent' && '/mo'}
                              </span>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(property)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(propId)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProperty ? t('editProperty') : t('addProperty')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div className="space-y-4">
              <Label>{t('propertyName')}</Label>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="English"
                  value={formData.title?.en || ''}
                  onChange={(e) => updateLocalizedField('title', 'en', e.target.value)}
                />
                <Input
                  placeholder="Русский"
                  value={formData.title?.ru || ''}
                  onChange={(e) => updateLocalizedField('title', 'ru', e.target.value)}
                />
                <Input
                  placeholder="O'zbek"
                  value={formData.title?.uz || ''}
                  onChange={(e) => updateLocalizedField('title', 'uz', e.target.value)}
                />
              </div>
            </div>

            {/* Type & Price */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={formData.type} onValueChange={(v) => updateFormField('type', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">{t('apartments')}</SelectItem>
                    <SelectItem value="house">{t('houses')}</SelectItem>
                    <SelectItem value="commercial">{t('commercial')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('price')}</Label>
<Input
  type="text"
  className="mt-2"
  value={formData.price}
  onChange={(e) => updateFormField('price', e.target.value)}
  placeholder="e.g., $500,000 or 500,000 UZS"
/>
              </div>
              <div>
                <Label>Sale/Rent</Label>
                <Select value={formData.price_type} onValueChange={(v) => updateFormField('price_type', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">{t('forSale')}</SelectItem>
                    <SelectItem value="rent">{t('forRent')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* District */}
            <div>
              <Label>{t('district')}</Label>
              <Select value={formData.district_id} onValueChange={(v) => updateFormField('district_id', v)}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select district" />
                </SelectTrigger>
                <SelectContent>
                  {districts.map((d) => (
                    <SelectItem key={d.district_id} value={d.district_id}>
                      {getLocalizedText(d.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <Label>{t('address')}</Label>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  placeholder="English address"
                  value={formData.address?.en || ''}
                  onChange={(e) => updateLocalizedField('address', 'en', e.target.value)}
                />
                <Input
                  placeholder="Адрес на русском"
                  value={formData.address?.ru || ''}
                  onChange={(e) => updateLocalizedField('address', 'ru', e.target.value)}
                />
                <Input
                  placeholder="Manzil o'zbekcha"
                  value={formData.address?.uz || ''}
                  onChange={(e) => updateLocalizedField('address', 'uz', e.target.value)}
                />
              </div>
            </div>

            {/* Details */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>{t('bedrooms')}</Label>
                <Input
                  type="number"
                  className="mt-2"
                  value={formData.bedrooms}
                  onChange={(e) => updateFormField('bedrooms', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label>{t('bathroom')}</Label>
                <Input
                  type="number"
                  className="mt-2"
                  value={formData.bathrooms}
                  onChange={(e) => updateFormField('bathrooms', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label>{t('area')} ({t('sqm')})</Label>
                <Input
                  type="number"
                  className="mt-2"
                  value={formData.area}
                  onChange={(e) => updateFormField('area', parseInt(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label>{t('year')}</Label>
                <Input
                  type="number"
                  className="mt-2"
                  value={formData.year}
                  onChange={(e) => updateFormField('year', parseInt(e.target.value) || 2024)}
                />
              </div>
            </div>

            {/* Parking */}
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.parking}
                onCheckedChange={(checked) => updateFormField('parking', checked)}
              />
              <Label>{t('parking')}</Label>
            </div>

{/* Description */}
<div className="space-y-4">
  <Label>{t('description')}</Label>
  <div className="space-y-3">
    <Textarea
      placeholder="English description"
      value={formData.description?.en || ''}
      onChange={(e) => updateLocalizedField('description', 'en', e.target.value)}
      rows={3}
    />
    <Textarea
      placeholder="Описание на русском"
      value={formData.description?.ru || ''}
      onChange={(e) => updateLocalizedField('description', 'ru', e.target.value)}
      rows={3}
    />
    <Textarea
      placeholder="Tavsif o'zbekcha"
      value={formData.description?.uz || ''}
      onChange={(e) => updateLocalizedField('description', 'uz', e.target.value)}
      rows={3}
    />
  </div>
{/* Image URLs (Multiple) */}
<div className="space-y-4">
  <Label>Image URLs (one per line)</Label>
  <Textarea
    className="mt-2"
    placeholder="placeholder.jpg"
    value={formData.images?.join('\n') || ''}
    onChange={(e) => updateFormField('images', e.target.value.split(/[\n,;]+/).filter(url => url.trim()))}
    rows={3}
  />
</div>

{/* Video URLs (Multiple) */}
<div className="space-y-4">
  <Label>Video URLs (optional, one per line)</Label>
  <Textarea
    className="mt-2"
    placeholder="https://youtube.com/video-1&#10;https://vimeo.com/video-2"
    value={formData.videos?.join('\n') || ''}
    onChange={(e) => updateFormField('videos', e.target.value.split('\n').filter(url => url.trim()))}
    rows={2}
  />
</div>
</div> 
            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Saving...' : t('save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminPage;
